import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { intakeSchema, type IntakeInput } from "./intake-schema";
import { optionalAuth, requireAuth, mapDbError } from "./auth.server";
import { FREE_GENERATION_LIMIT, incrementUsageCount, resolveUsageCount } from "./usage";
import process from "node:process";
import { randomUUID } from "node:crypto";

// ------------ Prompt builders ------------
function proposalPrompt(d: IntakeInput) {
  return `You are an expert proposal writer for freelancers and creative professionals. Generate a compelling, professional client proposal.

PROJECT DETAILS:
Project Type: ${d.project_type}
Freelancer Name: ${d.freelancer_name}
Client Name: ${d.client_name}
Project Title: ${d.project_title}
Scope: ${d.scope}
Deliverables: ${d.deliverables}
Start Date: ${d.start_date}
Deadline: ${d.end_date}
Total Investment: $${d.total_price}
Payment Terms: ${d.payment_terms}

Write a professional proposal with EXACTLY these section headers (write the header name in plain text, then the content below it):

INTRODUCTION
[2-3 sentences. Warm, personalized greeting to ${d.client_name}. Reference this specific project. Express genuine enthusiasm.]

PROJECT UNDERSTANDING
[1 paragraph. Show you deeply understand their goals and needs. Mirror back what they're trying to achieve. Be specific.]

SCOPE OF WORK
[Bulleted list of 5-8 specific, concrete deliverables based on the project details. Each bullet should be one clear sentence.]

PROJECT TIMELINE
[3-5 milestone entries. Format each as: Week X: [milestone]. Run from ${d.start_date} to ${d.end_date}.]

INVESTMENT
[State total: $${d.total_price}. Show payment breakdown based on ${d.payment_terms}. Briefly explain what the investment includes.]

WHY WORK WITH ME
[3 bullet points. Each is a specific, confident reason tailored to this exact type of project. Avoid generic claims.]

NEXT STEPS
[2-3 sentences. Tell ${d.client_name} exactly how to accept. Clear CTA.]

RULES: Professional but warm. No jargon. No filler phrases like "I am writing to". Be specific and concrete throughout. Total length: 500-650 words.`;
}

function contractPrompt(d: IntakeInput) {
  return `You are a freelance contract specialist. Generate a complete, professional, legally-sound freelance services agreement.

PROJECT DETAILS:
Project Type: ${d.project_type}
Freelancer: ${d.freelancer_name}
Client: ${d.client_name}
Project: ${d.project_title}
Scope: ${d.scope}
Deliverables: ${d.deliverables}
Start Date: ${d.start_date}
Completion Date: ${d.end_date}
Total Fee: $${d.total_price}
Payment Terms: ${d.payment_terms}

Generate a FREELANCE SERVICES AGREEMENT with these exact numbered sections. Be specific — use the actual names, dates, amounts, and deliverables from the project details above throughout the contract:

FREELANCE SERVICES AGREEMENT

This agreement is entered into as of ${d.start_date} between:
${d.freelancer_name} ("Freelancer") and ${d.client_name} ("Client").

1. PROJECT SCOPE AND DELIVERABLES
2. PROJECT TIMELINE
3. COMPENSATION AND PAYMENT SCHEDULE (state total fee: $${d.total_price}; late payments incur 1.5% monthly interest after 14 days)
4. REVISION POLICY (2 rounds included)
5. INTELLECTUAL PROPERTY AND OWNERSHIP (client receives ownership upon final payment; freelancer retains portfolio rights)
6. CONFIDENTIALITY
7. PROJECT CANCELLATION AND KILL FEE (25% kill fee on remaining unbilled balance)
8. INDEPENDENT CONTRACTOR STATUS
9. LIMITATION OF LIABILITY (limited to total project fee)
10. GOVERNING LAW (Freelancer's jurisdiction; mediation before litigation)
11. ENTIRE AGREEMENT

SIGNATURES

Freelancer: _______________________  Date: ____________
${d.freelancer_name}

Client: ___________________________  Date: ____________
${d.client_name}

---
DISCLAIMER: This contract was generated as a starting template. For projects over $5,000, consider having it reviewed by a licensed attorney in your jurisdiction.`;
}

const DEFAULT_GEMINI_MODEL = "gemini-2.0-flash";

async function callGemini(apiKey: string, model: string, prompt: string): Promise<string> {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-goog-api-key": apiKey },
    body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Gemini ${res.status}: ${t.slice(0, 300)}`);
  }
  const json = (await res.json()) as {
    candidates?: { content?: { parts?: { text?: string }[] } }[];
  };
  const text = json.candidates?.[0]?.content?.parts?.map((p) => p.text ?? "").join("") ?? "";
  if (!text) throw new Error("Gemini returned no content");
  return text.trim();
}

// ============== generateDocuments ==============
export const generateDocuments = createServerFn({ method: "POST" })
  .inputValidator(intakeSchema)
  .handler(async ({ data }) => {
    const geminiKey = process.env.GEMINI_API_KEY;
    if (!geminiKey) throw new Error("Missing GEMINI_API_KEY secret.");
    const geminiModel = process.env.GEMINI_MODEL || DEFAULT_GEMINI_MODEL;

    const auth = await optionalAuth();
    let userId: string | null = null;
    let plan: "free" | "pro" = "free";

    if (auth) {
      userId = auth.user.id;
      const { getSupabaseAdmin } = await import("@/integrations/supabase/admin.server");
      const admin = getSupabaseAdmin();
      const { data: profile } = await admin
        .from("profiles")
        .select("plan, docs_used_this_month, usage_reset_date")
        .eq("id", userId)
        .maybeSingle();

      if (profile) {
        const usage = await resolveUsageCount(admin, userId, profile);
        plan = usage.plan;
        if (plan === "free" && usage.used >= FREE_GENERATION_LIMIT) {
          return {
            error: "limit_reached" as const,
            proposal: null,
            contract: null,
            documentId: null,
          };
        }
      }
    }

    const [proposal, contract] = await Promise.all([
      callGemini(geminiKey, geminiModel, proposalPrompt(data)),
      callGemini(geminiKey, geminiModel, contractPrompt(data)),
    ]);

    let documentId: string | null = null;
    if (userId) {
      const { getSupabaseAdmin } = await import("@/integrations/supabase/admin.server");
      const admin = getSupabaseAdmin();

      const { data: doc, error: insertErr } = await admin
        .from("documents")
        .insert({
          user_id: userId,
          project_type: data.project_type,
          project_title: data.project_title,
          client_name: data.client_name,
          client_email: data.client_email ?? null,
          freelancer_name: data.freelancer_name,
          scope: data.scope,
          deliverables: data.deliverables,
          start_date: data.start_date,
          end_date: data.end_date,
          total_price: data.total_price,
          payment_terms: data.payment_terms,
          proposal_content: proposal,
          contract_content: contract,
        })
        .select("id")
        .single();

      if (insertErr) mapDbError(insertErr, "save document");
      documentId = doc?.id ?? null;

      await incrementUsageCount(admin, userId);
    }

    return { error: null, proposal, contract, documentId };
  });

// ============== getProfile ==============
export const getProfile = createServerFn({ method: "GET" }).handler(async () => {
  const auth = await optionalAuth();
  if (!auth) return null;
  const { data: profile, error } = await auth.userClient
    .from("profiles")
    .select(
      "id, email, full_name, plan, docs_used_this_month, usage_reset_date, stripe_customer_id, stripe_subscription_id",
    )
    .eq("id", auth.user.id)
    .maybeSingle();
  if (error) mapDbError(error, "load profile");
  return profile;
});

// ============== listDocuments ==============
export const listDocuments = createServerFn({ method: "GET" }).handler(async () => {
  const auth = await optionalAuth();
  if (!auth) return [];
  const { data, error } = await auth.userClient
    .from("documents")
    .select("id, project_title, project_type, client_name, total_price, status, created_at")
    .eq("user_id", auth.user.id)
    .order("created_at", { ascending: false });
  if (error) mapDbError(error, "list documents");
  return data ?? [];
});

// ============== getDocument ==============
export const getDocument = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const auth = await optionalAuth();
    if (!auth) return null;
    const { data: doc, error } = await auth.userClient
      .from("documents")
      .select("*")
      .eq("id", data.id)
      .eq("user_id", auth.user.id)
      .maybeSingle();
    if (error) mapDbError(error, "load document");
    return doc;
  });

// ============== updateDocument (edit proposal/contract content) ==============
export const updateDocument = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      id: z.string().uuid(),
      proposal_content: z.string().optional(),
      contract_content: z.string().optional(),
      status: z.enum(["draft", "sent", "accepted"]).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { user, userClient } = await requireAuth();
    const patch: Record<string, unknown> = {};
    if (data.proposal_content !== undefined) patch.proposal_content = data.proposal_content;
    if (data.contract_content !== undefined) patch.contract_content = data.contract_content;
    if (data.status !== undefined) patch.status = data.status;
    const { error } = await userClient
      .from("documents")
      .update(patch)
      .eq("id", data.id)
      .eq("user_id", user.id);
    if (error) mapDbError(error, "update document");
    return { ok: true };
  });

// ============== deleteDocument ==============
export const deleteDocument = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { user, userClient } = await requireAuth();
    const { error } = await userClient
      .from("documents")
      .delete()
      .eq("id", data.id)
      .eq("user_id", user.id);
    if (error) mapDbError(error, "delete document");
    return { ok: true };
  });

// ============== createShareLink ==============
export const createShareLink = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { user, userClient } = await requireAuth();
    const shareToken = randomUUID().replace(/-/g, "");
    const { error } = await userClient
      .from("documents")
      .update({ share_token: shareToken })
      .eq("id", data.id)
      .eq("user_id", user.id);
    if (error) mapDbError(error, "create share link");
    return { shareToken, sharePath: `/share/${shareToken}` };
  });

// ============== revokeShareLink ==============
export const revokeShareLink = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { user, userClient } = await requireAuth();
    const { error } = await userClient
      .from("documents")
      .update({ share_token: null })
      .eq("id", data.id)
      .eq("user_id", user.id);
    if (error) mapDbError(error, "revoke share link");
    return { ok: true };
  });

// ============== getSharedDocument (public, no auth) ==============
export const getSharedDocument = createServerFn({ method: "POST" })
  .inputValidator(z.object({ token: z.string().min(16).max(64) }))
  .handler(async ({ data }) => {
    const { getSupabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const admin = getSupabaseAdmin();
    const { data: doc, error } = await admin
      .from("documents")
      .select(
        "project_title, client_name, freelancer_name, project_type, total_price, start_date, end_date, proposal_content, contract_content",
      )
      .eq("share_token", data.token)
      .maybeSingle();
    if (error) throw new Error(`Share lookup failed: ${error.message}`);
    return doc;
  });

// ============== updateProfileName ==============
export const updateProfileName = createServerFn({ method: "POST" })
  .inputValidator(z.object({ full_name: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    const { user, userClient } = await requireAuth();
    const { error } = await userClient
      .from("profiles")
      .update({ full_name: data.full_name })
      .eq("id", user.id);
    if (error) mapDbError(error, "update profile");
    return { ok: true };
  });
